import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { getVersions, restoreVersion, compareVersions } from "../api";
import { C } from "../constants";
import Btn from "./Btn";
import { useAppNotify } from "./AppNotify";

/**
 * Inline plan version history (Plans → History).
 * Shows generation count, latest vs previous, and diffs.
 */
export default function PlanVersionPanel({ open, onRestored }) {
  const { notify, confirm } = useAppNotify();
  const [versions, setVersions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [msg, setMsg] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [liveDiff, setLiveDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const r = await getVersions();
      if (r.success) {
        setVersions(r.versions || []);
        setMeta(r.meta || null);
      } else {
        setVersions([]);
        setMeta(null);
      }
    } catch {
      setVersions([]);
      setMeta(null);
      setMsg({ type: "error", text: "Could not load versions. Are you logged in?" });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleRestore = async (versionNum) => {
    const ok = await confirm({
      title: `Rollback to Version ${versionNum}?`,
      message: "Your current schedule will be replaced with this older version as a new latest.",
      confirmLabel: "Rollback",
      cancelLabel: "Cancel",
      tone: "primary",
    });
    if (!ok) return;
    setRestoring(versionNum);
    setMsg(null);
    try {
      const res = await restoreVersion(versionNum);
      if (res.success) {
        const n = res.diff?.totalChanges;
        notify({
          type: "success",
          title: "Rollback complete",
          message: `Restored ${res.memberName || "Student"} as Version ${res.memberVersion || res.newVersion}${n != null ? ` (${n} row changes)` : ""}`,
        });
        if (typeof onRestored === "function") {
          onRestored(res.plan, res.newVersion);
        } else {
          try {
            window.dispatchEvent(new CustomEvent("ap:plan-restored", {
              detail: { plan: res.plan, newVersion: res.newVersion },
            }));
          } catch (_) { /* ignore */ }
        }
        try {
          const raw = localStorage.getItem("ap_plans");
          const plans = raw ? JSON.parse(raw) : {};
          if (res.plan) {
            plans.daily = res.plan;
            localStorage.setItem("ap_plans", JSON.stringify(plans));
          }
        } catch (_) { /* ignore */ }
        await load();
      } else {
        notify({ type: "error", title: "Rollback failed", message: res.error || "Restore failed" });
      }
    } catch (err) {
      notify({
        type: "error",
        title: "Rollback failed",
        message: err.response?.data?.error || err.message || "Restore failed",
      });
    }
    setRestoring(null);
  };

  const toggleDiff = async (v) => {
    if (expanded === v.version) {
      setExpanded(null);
      setLiveDiff(null);
      return;
    }
    setExpanded(v.version);
    setLiveDiff(null);

    // Prefer stored diff preview; if comparing with previous, fetch full compare
    if (v.previousVersion) {
      setDiffLoading(true);
      try {
        const res = await compareVersions(v.previousVersion, v.version);
        if (res.success) setLiveDiff(res.diff);
        else if (v.diff) setLiveDiff(v.diff);
      } catch {
        if (v.diff) setLiveDiff(v.diff);
      }
      setDiffLoading(false);
    } else if (v.diff) {
      setLiveDiff(v.diff);
    }
  };

  if (!open) return null;

  const latest = versions[0];
  const previous = versions[1];

  return (
    <div style={{ background: C.bg, borderRadius: 8, padding: "12px 14px", marginBottom: 14, border: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, gap: 8 }}>
        <div>
          <p style={{ fontSize: 12, fontWeight: 800, color: C.text, margin: "0 0 4px" }}>
            Version history
          </p>
          <p style={{ fontSize: 11, color: C.muted, margin: 0, lineHeight: 1.4 }}>
            {meta
              ? (meta.memberCount || 0) > 1
                ? `${meta.totalVersions} saved · ${meta.memberCount} students (each starts at V1)`
                : `${meta.totalVersions} saved · Latest ${latest ? `${latest.memberName || "Solo"} · V${latest.displayVersion || latest.memberVersion || "—"}` : "—"}`
              : "Version 1, Version 2, Version 3… after each generate or restore"}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            style={{
              fontSize: 11, fontWeight: 600, border: `1px solid ${C.border}`,
              background: C.surface, borderRadius: 6, padding: "4px 8px", cursor: "pointer", color: C.muted,
            }}
          >
            {loading ? "…" : "Refresh"}
          </button>
          <Link to="/versions" style={{ fontSize: 11, fontWeight: 700, color: C.primary, textDecoration: "none" }}>
            Full page →
          </Link>
        </div>
      </div>

      {latest && (
        <div style={{
          fontSize: 11, marginBottom: 10, padding: "8px 10px", borderRadius: 8,
          background: C.primarySoft, color: C.primaryDark, lineHeight: 1.45,
        }}>
          <strong>{latest.memberName || "Solo"} · Version {latest.displayVersion || latest.version}</strong>
          {latest.isLatest ? " (latest)" : ""}
          {latest.changedBy ? ` · by ${latest.changedBy}` : ""}
          {latest.changeType ? ` · ${latest.changeType}` : ""}
          {latest.differenceSummary || latest.diffSummary || latest.diff?.summary
            ? ` — ${latest.differenceSummary || latest.diffSummary || latest.diff.summary}`
            : ""}
          {latest.protected ? " · 🔒 protected" : ""}
        </div>
      )}

      {msg && (
        <div style={{
          fontSize: 12, marginBottom: 8, padding: "6px 10px", borderRadius: 6,
          background: msg.type === "error" ? "#FEF2F2" : "#ECFDF5",
          color: msg.type === "error" ? C.error : C.success,
        }}>
          {msg.text}
        </div>
      )}

      {loading && versions.length === 0 && (
        <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>Loading versions…</p>
      )}

      {!loading && versions.length === 0 && (
        <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>
          No saved versions yet. Generate a plan (or refine / regenerate) to create Version 1.
        </p>
      )}

      {versions.slice(0, 12).map((v) => {
        const when = v.createdAt ? new Date(v.createdAt).toLocaleString() : "";
        const openDiff = expanded === v.version;
        return (
          <div
            key={v.version}
            style={{
              padding: "8px 0",
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 700, color: C.text, fontSize: 12, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span>{v.memberName || "Solo"} · Version {v.displayVersion || v.version}</span>
                  <span style={{ fontWeight: 600, color: C.muted }}>· {v.changeType || "Update"}</span>
                  {v.changedBy && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: C.primaryDark }}>
                      by {v.changedBy}
                    </span>
                  )}
                  {v.protected && (
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 999,
                      background: C.warningBg, color: C.warning,
                    }}>
                      PROTECTED
                    </span>
                  )}
                  {v.isLatest && (
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 999,
                      background: C.successBg, color: C.success,
                    }}>
                      LATEST
                    </span>
                  )}
                  {(v.changeCount > 0 || v.diff?.totalChanges > 0) && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: C.warning }}>
                      {v.changeCount || v.diff.totalChanges}Δ
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: 11, fontStyle: "italic", color: C.muted,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {v.reason || "—"} {when ? `· ${when}` : ""}
                </div>
                {(v.differenceSummary || v.diffSummary || v.diff?.summary) && (
                  <div style={{ fontSize: 10, color: C.faint, marginTop: 2 }}>
                    vs v{v.previousVersion || "—"}: {v.differenceSummary || v.diffSummary || v.diff.summary}
                  </div>
                )}
                {v.triggerLabel && (
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                    Trigger: {v.triggerLabel}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <Btn
                  small
                  variant="ghost"
                  disabled={!v.previousVersion && !(v.diff?.changes?.length)}
                  onClick={() => toggleDiff(v)}
                >
                  {openDiff ? "Hide" : "Diff"}
                </Btn>
                {!v.isLatest && (
                  <Btn
                    small
                    variant="outline"
                    disabled={restoring === v.version}
                    onClick={() => handleRestore(v.version)}
                  >
                    {restoring === v.version ? "…" : "Restore"}
                  </Btn>
                )}
              </div>
            </div>

            {openDiff && (
              <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}` }}>
                {diffLoading && <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>Loading diff…</p>}
                {!diffLoading && liveDiff && (
                  <>
                    <p style={{ fontSize: 11, fontWeight: 700, color: C.text, margin: "0 0 6px" }}>
                      {liveDiff.summary || `${liveDiff.totalChanges || 0} changes`}
                      {v.previousVersion ? ` (v${v.previousVersion} → v${v.version})` : ""}
                    </p>
                    {(liveDiff.changes || []).slice(0, 6).map((c, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: 11, marginBottom: 4, padding: "4px 8px", borderRadius: 6,
                          background: c.type === "added" ? C.successBg : c.type === "removed" ? C.errorBg : C.warningBg,
                          color: c.type === "added" ? C.success : c.type === "removed" ? C.error : C.warning,
                        }}
                      >
                        <strong>{String(c.type || "").toUpperCase()}</strong>
                        {" — "}
                        {c.day || "?"}
                        {c.time ? ` @ ${c.time}` : ""}
                        {c.activity ? `: ${c.activity}` : ""}
                      </div>
                    ))}
                    {(liveDiff.changes?.length || 0) > 6 && (
                      <p style={{ fontSize: 10, color: C.muted, margin: "4px 0 0" }}>
                        …and {liveDiff.changes.length - 6} more — open full page to compare
                      </p>
                    )}
                    {!liveDiff.totalChanges && (
                      <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>No row differences stored for this version.</p>
                    )}
                  </>
                )}
                {!diffLoading && !liveDiff && (
                  <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>No diff available for this version.</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
